import { TSession } from "./types";

const PERSONAL_EMAIL_EXACT_DOMAINS = new Set([
  "aol.com",
  "comcast.net",
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "icloud.com",
  "live.com",
  "mac.com",
  "mail.ru",
  "me.com",
  "msn.com",
  "outlook.com",
  "pm.me",
  "proton.me",
  "protonmail.com",
  "qq.com",
  "yandex.com",
  "yandex.ru",
  "yahoo.com",
  "ymail.com",
]);

const PERSONAL_EMAIL_REGIONAL_PROVIDERS = new Set([
  "hotmail",
  "live",
  "outlook",
  "yahoo",
]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CLINIC_EMAIL_VERIFICATION_MAX_ACCOUNT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const getStorageValue = (key: string): string | null => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
};

const setStorageValue = (key: string, value: string) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, value);
};

const getDismissedStorageKey = (userId: string) =>
  `clinic_email_verification_nudge_dismissed_${userId}`;

const getSubmittedStorageKey = (userId: string) =>
  `clinic_email_verification_submitted_${userId}`;

export const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const getEmailDomain = (email: string): string | null => {
  const normalizedEmail = normalizeEmail(email);
  if (!EMAIL_PATTERN.test(normalizedEmail)) return null;
  return normalizedEmail.split("@").pop() || null;
};

export const isPersonalEmailDomain = (domain: string | null) =>
  Boolean(
    domain &&
      (PERSONAL_EMAIL_EXACT_DOMAINS.has(domain) ||
        PERSONAL_EMAIL_REGIONAL_PROVIDERS.has(domain.split(".")[0] || "")),
  );

export const isPersonalEmail = (email: string) =>
  isPersonalEmailDomain(getEmailDomain(email));

export const validateClinicEmail = (email: string): string | null => {
  const normalizedEmail = normalizeEmail(email);
  if (!EMAIL_PATTERN.test(normalizedEmail)) {
    return "Enter a valid clinic email address.";
  }
  if (isPersonalEmail(normalizedEmail)) {
    return "Use a clinic email, not a personal email address.";
  }
  return null;
};

export const hasDismissedClinicEmailVerificationNudge = (userId?: string) => {
  if (!userId) return false;
  return getStorageValue(getDismissedStorageKey(userId)) === "true";
};

export const hasSubmittedClinicEmailVerification = (userId?: string) => {
  if (!userId) return false;
  return getStorageValue(getSubmittedStorageKey(userId)) === "true";
};

export const markClinicEmailVerificationNudgeDismissed = (userId?: string) => {
  if (!userId) return;
  setStorageValue(getDismissedStorageKey(userId), "true");
};

export const markClinicEmailVerificationSubmitted = (userId?: string) => {
  if (!userId) return;
  setStorageValue(getSubmittedStorageKey(userId), "true");
};

export const isWithinClinicEmailVerificationAccountAgeWindow = (
  createdAt?: string,
  nowMs = Date.now(),
) => {
  if (!createdAt) return false;
  const createdAtMs = new Date(createdAt).getTime();
  if (!Number.isFinite(createdAtMs)) return false;
  return nowMs - createdAtMs <= CLINIC_EMAIL_VERIFICATION_MAX_ACCOUNT_AGE_MS;
};

export const shouldShowClinicEmailVerificationNudge = (
  session?: TSession | null,
) => {
  if (!session?.userId || !session.userEmail) return false;
  if (session.role === "PATIENT") return false;
  if (session.planType !== "FREE") return false;
  if (session.websiteUrl?.trim()) return false;
  if (!isWithinClinicEmailVerificationAccountAgeWindow(session.createdAt)) {
    return false;
  }
  if (!isPersonalEmail(session.userEmail)) return false;
  if (hasDismissedClinicEmailVerificationNudge(session.userId)) return false;
  if (hasSubmittedClinicEmailVerification(session.userId)) return false;
  return true;
};
